import { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import {
  User,
  Mail,
  Phone,
  MapPin,
  Pencil,
  Trash2,
  Plus,
  RotateCcw,
  Star,
  ChevronDown,
  ChevronUp,
  ShoppingBag,
  Truck,
  UtensilsCrossed,
  Check,
  LogOut,
  ShieldCheck,
  Loader2,
} from "lucide-react";
import Header from "@/components/shared/Header";
import Footer from "@/components/shared/Footer";
import { useLanguage } from "@/contexts/LanguageContext";
import { useCart, CartItem } from "@/contexts/CartContext";
import { useFormatPrice } from "@/utils/currency";
import { sampleOrders, menuItems } from "@/data/mockData";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Progress } from "@/components/ui/progress";
import { Skeleton } from "@/components/ui/skeleton";
import { useCustomerAuth } from "@/contexts/CustomerAuthContext";
import {
  getUserProfile,
  updateUserProfile,
  type UserProfileResponse,
  type APIError,
} from "@/services/authApi";
import { useToast } from "@/hooks/use-toast";
import { useBrand } from "@/contexts/BrandContext";
import { useCustomer } from "@/hooks/useCustomer";
import AddAddressDialog from "@/components/profile/AddAddressDialog";
import type { AddressData } from "@/services/defaultSession";
import { useAuthGate } from "@/hooks/useAuthGate";
import { OrderStatusCode } from "@/hooks/useFirebaseOrderStatus";

const ITEMS_PER_PAGE = 4;

/* ------------------------------------------------------------------ */
/*  Skeleton for the profile page                                      */
/* ------------------------------------------------------------------ */

const STATUS_LABEL_EN: Record<OrderStatusCode, string> = {
  0: "Order Placed",
  1: "Rejected",
  2: "Accepted",
  3: "Preparing",
  4: "Ready",
  5: "On the Way",
  6: "Delivered",
  7: "Pending",
  8: "Handed to Driver",
};

const STATUS_LABEL_AR: Record<OrderStatusCode, string> = {
  0: "تم الطلب",
  1: "مرفوض",
  2: "مقبول",
  3: "جاري التحضير",
  4: "جاهز",
  5: "في الطريق",
  6: "تم التوصيل",
  7: "قيد الانتظار",
  8: "تم التسليم للسائق",
};

function ProfileSkeleton() {
  return (
    <main className="flex-1 container mx-auto px-4 py-6">
      <div className="flex items-center justify-between mb-6">
        <Skeleton className="h-8 w-40" />
        <Skeleton className="h-9 w-24" />
      </div>
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <div className="space-y-4">
          {/* Profile card skeleton */}
          <div className="rounded-xl border border-border bg-card p-5 space-y-4">
            <div className="flex items-center justify-between">
              <Skeleton className="h-5 w-32" />
              <Skeleton className="h-8 w-8 rounded" />
            </div>
            <div className="flex items-center gap-4">
              <Skeleton className="h-16 w-16 rounded-full" />
              <div className="space-y-2 flex-1">
                <Skeleton className="h-5 w-28" />
                <Skeleton className="h-5 w-16" />
              </div>
            </div>
            <div className="space-y-3">
              <div className="flex items-center gap-3">
                <Skeleton className="h-4 w-4" />
                <Skeleton className="h-4 w-40" />
              </div>
              <div className="flex items-center gap-3">
                <Skeleton className="h-4 w-4" />
                <Skeleton className="h-4 w-36" />
              </div>
            </div>
          </div>
          {/* Addresses skeleton */}
          <div className="rounded-xl border border-border bg-card p-5 space-y-4">
            <Skeleton className="h-5 w-36" />
            <div className="space-y-2">
              <Skeleton className="h-16 w-full rounded-lg" />
              <Skeleton className="h-16 w-full rounded-lg" />
            </div>
          </div>
          {/* Loyalty skeleton */}
          <div className="rounded-xl border border-border bg-card p-5 space-y-4">
            <Skeleton className="h-5 w-28" />
            <div className="flex flex-col items-center gap-2">
              <Skeleton className="h-10 w-20" />
              <Skeleton className="h-3 w-16" />
            </div>
            <Skeleton className="h-2 w-full" />
            <div className="space-y-1.5">
              {[1, 2, 3].map((i) => (
                <Skeleton key={i} className="h-4 w-full" />
              ))}
            </div>
          </div>
        </div>
        {/* Orders skeleton */}
        <div className="lg:col-span-2">
          <div className="rounded-xl border border-border bg-card">
            <div className="p-5 border-b border-border">
              <Skeleton className="h-6 w-32" />
            </div>
            {[1, 2, 3].map((i) => (
              <div
                key={i}
                className="p-4 border-b border-border last:border-b-0 space-y-2"
              >
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <Skeleton className="h-5 w-14" />
                    <Skeleton className="h-5 w-20" />
                    <Skeleton className="h-5 w-16" />
                  </div>
                  <Skeleton className="h-5 w-16" />
                </div>
                <Skeleton className="h-3 w-48" />
                <Skeleton className="h-3 w-64" />
              </div>
            ))}
          </div>
        </div>
      </div>
    </main>
  );
}

/* ------------------------------------------------------------------ */
/*  Guest View — prompts login                                         */
/* ------------------------------------------------------------------ */

function GuestProfileView() {
  const { language } = useLanguage();
  const { openLogin } = useCustomerAuth();
  const isAr = language === "ar";

  return (
    <div className="flex-1 flex items-center justify-center py-20">
      <div className="text-center space-y-6 max-w-sm mx-auto px-4">
        <div className="mx-auto w-20 h-20 rounded-full bg-primary/10 flex items-center justify-center">
          <ShieldCheck className="w-10 h-10 text-primary" />
        </div>
        <div className="space-y-2">
          <h2 className="text-2xl font-heading font-bold text-foreground">
            {isAr ? "سجل دخولك" : "Login to your account"}
          </h2>
          <p className="text-muted-foreground text-sm">
            {isAr
              ? "سجل دخولك لعرض ملفك الشخصي وسجل الطلبات ونقاط الولاء"
              : "Login to view your profile, order history, and loyalty points"}
          </p>
        </div>
        <Button size="lg" className="w-full gap-2" onClick={() => openLogin()}>
          <Phone className="h-5 w-5" />
          {isAr ? "تسجيل الدخول برقم الجوال" : "Login with phone number"}
        </Button>
      </div>
    </div>
  );
}

/* ------------------------------------------------------------------ */
/*  Main Profile Page                                                   */
/* ------------------------------------------------------------------ */

export default function ProfilePage() {
  const { t, language } = useLanguage();
  const { addItem } = useCart();
  const formatPrice = useFormatPrice();
  const navigate = useNavigate();
  const { requireAuth } = useAuthGate();

  const {
    isAuthenticated,
    customer,
    customerName,
    customerMobile,
    handleLogout,
    token,
  } = useCustomerAuth();
  const { toast } = useToast();
  const { brand } = useBrand();
  const { updateCustomerData } = useCustomer();

  const isAr = language === "ar";

  // Backend profile state
  const [profileLoading, setProfileLoading] = useState(true);
  const [profileData, setProfileData] = useState<
    UserProfileResponse["data"]["customer"] | null
  >(null);
  const [saving, setSaving] = useState(false);

  const fetchProfile = useCallback(async () => {
    if (!token) return;
    setProfileLoading(true);
    try {
      // Pass the customer mobile to the endpoint so it can lookup/create by number
      const mobile = customer?.mobile || "";
      const result = await getUserProfile(token, mobile, brand.id);
      if (result.success) {
        setProfileData(result.data.customer);
        // Update session with profile data so ChatWidget and other components can access the name
        console.log(
          "[ProfilePage] API response customer:",
          result.data.customer,
        );
        if (result.data.customer.first_name) {
          console.log(
            "[ProfilePage] Updating session with first_name:",
            result.data.customer.first_name,
          );
          updateCustomerData({
            first_name: result.data.customer.first_name,
            last_name: result.data.customer.last_name,
            email: result.data.customer.email,
          });
        } else {
          console.log(
            "[ProfilePage] No first_name in API response, skipping session update",
          );
        }
      }
    } finally {
      setProfileLoading(false);
    }
  }, [token, customer?.mobile, brand.id, updateCustomerData]);

  useEffect(() => {
    if (isAuthenticated && token) {
      fetchProfile();
    } else {
      setProfileLoading(false);
    }
  }, [isAuthenticated, token, fetchProfile]);

  // Derived display values from backend data
  const firstName = profileData?.first_name || customer?.first_name || "";
  const lastName = profileData?.last_name || customer?.last_name || "";
  const displayName = firstName
    ? `${firstName} ${lastName}`.trim()
    : isAr
      ? "لم يتم الإضافة"
      : "Not set";
  const displayEmail = profileData?.email || customer?.email || "";
  const displayPhone =
    profileData?.mobile || customer?.mobile || customerMobile || "";

  // Editable form state
  const [editingProfile, setEditingProfile] = useState(false);
  const [formFirstName, setFormFirstName] = useState("");
  const [formLastName, setFormLastName] = useState("");
  const [formEmail, setFormEmail] = useState("");

  // Sync form when profileData loads or editing starts
  useEffect(() => {
    if (profileData) {
      setFormFirstName(profileData.first_name || "");
      setFormLastName(profileData.last_name || "");
      setFormEmail(profileData.email || "");
    } else if (customer) {
      setFormFirstName(customer.first_name || "");
      setFormLastName(customer.last_name || "");
      setFormEmail(customer.email || "");
    }
  }, [profileData, customer]);

  const handleSaveProfile = useCallback(async () => {
    if (!token) return;
    setSaving(true);
    try {
      const result = await updateUserProfile(
        token,
        displayPhone,
        {
          first_name: formFirstName,
          last_name: formLastName,
          email: formEmail || undefined,
          country_code:
            profileData?.country_code || customer?.country_code || undefined,
        },
        brand.id,
      );
      if (result.success) {
        setProfileData((prev) =>
          prev
            ? {
                ...prev,
                first_name: result.data.customer.first_name,
                last_name: result.data.customer.last_name,
                email: result.data.customer.email,
              }
            : prev,
        );
        setEditingProfile(false);
        toast({ title: isAr ? "تم حفظ البيانات" : "Profile updated" });
      } else {
        toast({
          title: (result as APIError).error?.message || "Error",
          variant: "destructive",
        });
      }
    } finally {
      setSaving(false);
    }
  }, [token, formFirstName, formLastName, formEmail, isAr, toast]);

  const handleToggleEdit = () => {
    if (editingProfile) {
      handleSaveProfile();
    } else {
      setEditingProfile(true);
    }
  };

  // Loyalty from backend
  const loyalty = profileData?.loyalty || {
    points: 0,
    tier: "bronze",
    tier_ar: "برونزي",
    next_tier: "silver",
    next_tier_ar: "فضي",
    points_to_next: 500,
    total_to_next: 500,
    recent_earnings: [],
  };

  // Orders from backend
  const backendOrders = profileData?.recent_orders || null;

  // Addresses from backend
  const backendAddresses: Array<{
    id: string;
    label: string;
    labelAr: string;
    address: string;
    addressAr: string;
    raw?: any;
  }> =
    profileData?.addresses?.map((a: any) => ({
      id: a.id,
      label: a.label,
      labelAr: a.label_ar || a.label,
      address: a.address,
      addressAr: a.address_ar || a.address,
      raw: a,
    })) || [];

  const [addresses, setAddresses] = useState(backendAddresses);
  const [addAddressOpen, setAddAddressOpen] = useState(false);
  const [editAddressIndex, setEditAddressIndex] = useState<number | null>(null);

  // Session address helpers
  const {
    addAddress: sessionAddAddress,
    updateAddress: sessionUpdateAddress,
    removeAddress: sessionRemoveAddress,
  } = useCustomer();

  // Sync addresses when profileData loads
  useEffect(() => {
    if (profileData?.addresses?.length) {
      setAddresses(
        profileData.addresses.map((a: any, index: number) => ({
          id: a.id || `addr-${index}`,
          label: a.streetName || (isAr ? "عنوان" : "Address"),
          labelAr: a.streetName || "عنوان",
          address: a.geocodeAddress || "",
          addressAr: a.geocodeAddress || "",
          raw: a,
        })),
      );
    }
  }, [profileData, isAr]);

  const persistAddressesToBackend = useCallback(
    async (newAddresses: any[]) => {
      if (!token || !displayPhone) return;
      // Extract raw AddressData from each entry
      const rawList = newAddresses.map((a: any) => a.raw || a);
      try {
        await updateUserProfile(
          token,
          displayPhone,
          {
            addresses: rawList,
            country_code:
              profileData?.country_code || customer?.country_code || undefined,
          },
          brand.id,
        );
      } catch (e) {
        console.error("Failed to persist addresses:", e);
      }
    },
    [
      token,
      displayPhone,
      profileData?.country_code,
      customer?.country_code,
      brand.id,
    ],
  );

  const handleSaveAddress = (addressData: AddressData) => {
    let nextAddresses: any[];
    if (editAddressIndex !== null) {
      sessionUpdateAddress(editAddressIndex, addressData);
      nextAddresses = addresses.map((a: any, i: number) =>
        i === editAddressIndex
          ? {
              id: a.id || `addr-${editAddressIndex}`,
              label: addressData.streetName || (isAr ? "عنوان" : "Address"),
              labelAr: addressData.streetName || "عنوان",
              address: addressData.geocodeAddress || "",
              addressAr: addressData.geocodeAddress || "",
              raw: addressData,
            }
          : a,
      );
      setAddresses(nextAddresses);
      setEditAddressIndex(null);
    } else {
      sessionAddAddress(addressData);
      const newEntry = {
        id: `addr-${Date.now()}`,
        label: addressData.streetName || (isAr ? "عنوان" : "Address"),
        labelAr: addressData.streetName || "عنوان",
        address: addressData.geocodeAddress || "",
        addressAr: addressData.geocodeAddress || "",
        raw: addressData,
      };
      nextAddresses = [...addresses, newEntry];
      setAddresses(nextAddresses);
    }
    persistAddressesToBackend(nextAddresses);
    toast({ title: isAr ? "تم حفظ العنوان" : "Address saved" });
  };

  const [expandedOrder, setExpandedOrder] = useState<string | null>(null);
  const [currentPage, setCurrentPage] = useState(1);

  // Merge backend orders with fallback
  const displayOrders = backendOrders
    ? backendOrders.map((o: any) => ({
        id: o.id,
        number: o.order_number,
        type: o.order_type,
        items: o.items.map((i: any) => ({
          menuItemId: i.menu_item_id,
          name: i.name,
          quantity: i.quantity,
          price: i.price,
        })),
        total: o.total,
        status: o.status,
        date: o.date,
        paymentMethod: o.payment_method,
      }))
    : sampleOrders;

  const totalPages = Math.ceil(displayOrders.length / ITEMS_PER_PAGE);
  const paginatedOrders = displayOrders.slice(
    (currentPage - 1) * ITEMS_PER_PAGE,
    currentPage * ITEMS_PER_PAGE,
  );

  const handleReorder = (order: (typeof displayOrders)[0]) => {
    order.items.forEach((orderItem: any) => {
      const menuItem = menuItems.find((m) => m.id === orderItem.menuItemId);
      if (menuItem) {
        const cartItem: CartItem = {
          id: "",
          menuItem,
          quantity: orderItem.quantity,
          extras: [],
          removed: [],
          specialInstructions: "",
          unitPrice: orderItem.price,
          selectedFeatures: orderItem.selected_features || [],
          featureSummary: orderItem.feature_summary || [],
        };
        addItem(cartItem);
      }
    });
    requireAuth(() => navigate("/cart"));
  };

  const deleteAddress = (id: string, index: number) => {
    sessionRemoveAddress(index);
    const nextAddresses = addresses.filter((a: any) => a.id !== id);
    setAddresses(nextAddresses);
    persistAddressesToBackend(nextAddresses);
  };

  const orderTypeIcon = (type: string) => {
    switch (type) {
      case "dine-in":
        return <UtensilsCrossed className="h-3.5 w-3.5" />;
      case "delivery":
        return <Truck className="h-3.5 w-3.5" />;
      default:
        return <ShoppingBag className="h-3.5 w-3.5" />;
    }
  };

  const statusColor = (status: string) => {
    switch (status) {
      case "completed":
        return "bg-success text-success-foreground";
      case "cancelled":
        return "bg-destructive text-destructive-foreground";
      case "preparing":
        return "bg-warning text-warning-foreground";
      default:
        return "bg-primary text-primary-foreground";
    }
  };

  const tierColors: Record<string, string> = {
    bronze: "text-orange-600",
    silver: "text-muted-foreground",
    gold: "text-yellow-500",
    Bronze: "text-orange-600",
    Silver: "text-muted-foreground",
    Gold: "text-yellow-500",
  };

  return (
    <div className="min-h-screen flex flex-col">
      <Header />

      {!isAuthenticated ? (
        <GuestProfileView />
      ) : profileLoading ? (
        <ProfileSkeleton />
      ) : (
        <main className="flex-1 container mx-auto px-4 py-6">
          <div className="flex items-center justify-between mb-6">
            <h1 className="font-heading text-2xl md:text-3xl font-bold text-foreground">
              {t("profile.title")}
            </h1>
            <Button
              variant="outline"
              size="sm"
              className="gap-1.5 text-destructive hover:text-destructive"
              onClick={handleLogout}
            >
              <LogOut className="h-4 w-4" />
              {isAr ? "خروج" : "Logout"}
            </Button>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
            {/* Left Column: Profile + Addresses */}
            <div className="space-y-4">
              {/* Profile Card */}
              <div className="rounded-xl border border-border bg-card p-5">
                <div className="flex items-center justify-between mb-4">
                  <h2 className="font-heading font-bold text-foreground flex items-center gap-2">
                    <User className="h-5 w-5 text-primary" />
                    {t("profile.info")}
                  </h2>
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={handleToggleEdit}
                    disabled={saving}
                  >
                    {saving ? (
                      <Loader2 className="h-4 w-4 animate-spin" />
                    ) : editingProfile ? (
                      <Check className="h-4 w-4" />
                    ) : (
                      <Pencil className="h-4 w-4" />
                    )}
                  </Button>
                </div>

                {/* Avatar */}
                <div className="flex items-center gap-4 mb-4">
                  <div className="flex h-16 w-16 items-center justify-center rounded-full bg-primary/10 text-2xl font-bold text-primary">
                    {(formFirstName || displayName).charAt(0)}
                  </div>
                  <div>
                    {editingProfile ? (
                      <div className="space-y-1">
                        <Input
                          value={formFirstName}
                          onChange={(e) => setFormFirstName(e.target.value)}
                          className="h-8"
                          placeholder={isAr ? "الاسم الأول" : "First name"}
                        />
                        <Input
                          value={formLastName}
                          onChange={(e) => setFormLastName(e.target.value)}
                          className="h-8"
                          placeholder={isAr ? "اسم العائلة" : "Last name"}
                        />
                      </div>
                    ) : (
                      <p className="font-heading font-bold text-foreground">
                        {displayName}
                      </p>
                    )}
                    <Badge
                      variant="secondary"
                      className={`text-xs mt-1 ${tierColors[loyalty.tier] || ""}`}
                    >
                      <Star className="h-3 w-3 me-1" />
                      {isAr
                        ? (loyalty as any).tier_ar || loyalty.tier
                        : loyalty.tier}
                    </Badge>
                  </div>
                </div>

                <div className="space-y-3">
                  <div className="flex items-center gap-3 text-sm">
                    <Mail className="h-4 w-4 text-muted-foreground shrink-0" />
                    {editingProfile ? (
                      <Input
                        value={formEmail}
                        onChange={(e) => setFormEmail(e.target.value)}
                        className="h-8"
                        placeholder={isAr ? "البريد الإلكتروني" : "Email"}
                        type="email"
                      />
                    ) : (
                      <span className="text-foreground">
                        {displayEmail || (isAr ? "لم يتم الإضافة" : "Not set")}
                      </span>
                    )}
                  </div>
                  <div className="flex items-center gap-3 text-sm">
                    <Phone className="h-4 w-4 text-muted-foreground shrink-0" />
                    <span className="text-foreground" dir="ltr">
                      {displayPhone || (isAr ? "لم يتم الإضافة" : "Not set")}
                    </span>
                  </div>
                </div>
              </div>

              {/* Saved Addresses */}
              <div className="rounded-xl border border-border bg-card p-5">
                <div className="flex items-center justify-between mb-4">
                  <h2 className="font-heading font-bold text-foreground flex items-center gap-2">
                    <MapPin className="h-5 w-5 text-primary" />
                    {t("profile.addresses")}
                  </h2>
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => {
                      setEditAddressIndex(null);
                      setAddAddressOpen(true);
                    }}
                  >
                    <Plus className="h-4 w-4" />
                  </Button>
                </div>
                <div className="space-y-2">
                  {addresses.map((addr: any, idx: number) => (
                    <div
                      key={addr.id}
                      className="flex items-start justify-between gap-2 rounded-lg border border-border p-3"
                    >
                      <div>
                        <p className="text-sm font-medium text-foreground">
                          {language === "ar" ? addr.labelAr : addr.label}
                        </p>
                        <p className="text-xs text-muted-foreground mt-0.5">
                          {language === "ar" ? addr.addressAr : addr.address}
                        </p>
                      </div>
                      <div className="flex gap-1 shrink-0">
                        <button
                          className="text-muted-foreground hover:text-foreground transition-colors"
                          onClick={() => {
                            setEditAddressIndex(idx);
                            setAddAddressOpen(true);
                          }}
                        >
                          <Pencil className="h-3.5 w-3.5" />
                        </button>
                        <button
                          className="text-muted-foreground hover:text-destructive transition-colors"
                          onClick={() => deleteAddress(addr.id, idx)}
                        >
                          <Trash2 className="h-3.5 w-3.5" />
                        </button>
                      </div>
                    </div>
                  ))}
                  {addresses.length === 0 && (
                    <p className="text-sm text-muted-foreground text-center py-4">
                      {t("profile.noAddresses")}
                    </p>
                  )}
                </div>
              </div>

              <AddAddressDialog
                open={addAddressOpen}
                onOpenChange={setAddAddressOpen}
                onSave={handleSaveAddress}
                editAddress={
                  editAddressIndex !== null
                    ? addresses[editAddressIndex]?.raw
                    : null
                }
                editIndex={editAddressIndex ?? undefined}
              />

              {/* Loyalty Dashboard */}
              {/* <div className="rounded-xl border border-border bg-card p-5">
                <h2 className="font-heading font-bold text-foreground flex items-center gap-2 mb-4">
                  <Star className="h-5 w-5 text-warning" />
                  {t("profile.loyalty")}
                </h2>

                <div className="text-center mb-4">
                  <p className="font-heading text-4xl font-extrabold text-primary">
                    {(loyalty.points ?? 0).toLocaleString()}
                  </p>
                  <p className="text-sm text-muted-foreground">
                    {t("profile.points")}
                  </p>
                </div>

                <div className="mb-4">
                  <div className="flex items-center justify-between text-xs mb-1">
                    <span
                      className={`font-medium ${tierColors[loyalty.tier] || ""}`}
                    >
                      {isAr
                        ? (loyalty as any).tier_ar || loyalty.tier
                        : loyalty.tier}
                    </span>
                    <span
                      className={`font-medium ${tierColors[(loyalty as any).next_tier] || ""}`}
                    >
                      {isAr
                        ? (loyalty as any).next_tier_ar ||
                          (loyalty as any).next_tier
                        : (loyalty as any).next_tier}
                    </span>
                  </div>
                  <Progress
                    value={
                      (((loyalty as any).total_to_next -
                        (loyalty as any).points_to_next) /
                        (loyalty as any).total_to_next) *
                      100
                    }
                    className="h-2"
                  />
                  <p className="text-xs text-muted-foreground mt-1 text-center">
                    {(loyalty as any).points_to_next}{" "}
                    {t("profile.pointsToNext")}{" "}
                    {isAr
                      ? (loyalty as any).next_tier_ar
                      : (loyalty as any).next_tier}
                  </p>
                </div>

                <h3 className="text-sm font-medium text-foreground mb-2">
                  {t("profile.recentEarnings")}
                </h3>
                <div className="space-y-1.5">
                  {((loyalty as any).recent_earnings || []).map(
                    (earning: any) => (
                      <div
                        key={earning.order_id}
                        className="flex items-center justify-between text-xs"
                      >
                        <span className="text-muted-foreground">
                          {earning.order_id} · {earning.date}
                        </span>
                        <span className="font-medium text-success">
                          +{earning.points} pts
                        </span>
                      </div>
                    ),
                  )}
                </div>
              </div> */}
            </div>

            {/* Right Column: Order History */}
            <div className="lg:col-span-2">
              <div className="rounded-xl border border-border bg-card">
                <div className="p-5 border-b border-border">
                  <h2 className="font-heading font-bold text-foreground text-lg">
                    {t("profile.orderHistory")}
                  </h2>
                </div>

                {paginatedOrders.map((order: any) => (
                  <div
                    key={order.id}
                    className="border-b border-border last:border-b-0"
                  >
                    <div className="p-4">
                      <div className="flex items-center justify-between gap-3">
                        <div className="flex items-center gap-3">
                          <span className="font-heading font-bold text-foreground">
                            {order.number}
                          </span>
                          <Badge variant="secondary" className="gap-1 text-xs">
                            {orderTypeIcon(order.type)}
                            {order.type === "dine-in"
                              ? t("order.dinein")
                              : order.type === "delivery"
                                ? t("order.delivery")
                                : t("order.takeaway")}
                          </Badge>
                          <Badge
                            className={`text-xs capitalize ${statusColor(order.status)}`}
                          >
                            {order.status.replace("-", " ")}
                            {language === "ar"
                              ? STATUS_LABEL_AR[order.statusCode]
                              : STATUS_LABEL_EN[order.statusCode]}
                          </Badge>
                        </div>
                        <div className="flex items-center gap-2">
                          <span className="font-heading font-bold text-primary">
                            {formatPrice(order.total)}
                          </span>
                          <button
                            onClick={() =>
                              setExpandedOrder(
                                expandedOrder === order.id ? null : order.id,
                              )
                            }
                            className="text-muted-foreground hover:text-foreground transition-colors"
                          >
                            {expandedOrder === order.id ? (
                              <ChevronUp className="h-4 w-4" />
                            ) : (
                              <ChevronDown className="h-4 w-4" />
                            )}
                          </button>
                        </div>
                      </div>

                      <div className="flex items-center gap-3 mt-1.5 text-xs text-muted-foreground">
                        <span>
                          {new Date(order.date).toLocaleDateString(
                            language === "ar" ? "ar-SA" : "en-US",
                            {
                              dateStyle: "medium",
                            },
                          )}
                        </span>
                        <span>·</span>
                        <span>
                          {new Date(order.date).toLocaleTimeString(
                            language === "ar" ? "ar-SA" : "en-US",
                            {
                              timeStyle: "short",
                            },
                          )}
                        </span>
                        <span>·</span>
                        <span>{order.paymentMethod}</span>
                      </div>

                      {expandedOrder !== order.id && (
                        <p className="text-xs text-muted-foreground mt-1 truncate">
                          {order.items
                            .map((i: any) => `${i.quantity}x ${i.name}`)
                            .join(", ")}
                        </p>
                      )}
                    </div>

                    {expandedOrder === order.id && (
                      <div className="px-4 pb-4">
                        <div className="rounded-lg bg-muted/50 p-3 space-y-2">
                          {order.items.map((item: any, idx: number) => (
                            <div
                              key={idx}
                              className="flex items-center justify-between text-sm"
                            >
                              <span className="text-foreground">
                                {item.quantity}x {item.name}
                              </span>
                              <span className="text-muted-foreground">
                                {formatPrice(item.price * item.quantity)}
                              </span>
                            </div>
                          ))}
                        </div>
                        {(order.status === "completed" ||
                          order.status === "cancelled") && (
                          <Button
                            size="sm"
                            className="mt-3 gap-1.5"
                            onClick={() => handleReorder(order)}
                          >
                            <RotateCcw className="h-3.5 w-3.5" />
                            {t("profile.reorder")}
                          </Button>
                        )}
                      </div>
                    )}
                  </div>
                ))}
              </div>

              {totalPages > 1 && (
                <div className="flex items-center justify-center gap-2 mt-4">
                  {Array.from({ length: totalPages }, (_, i) => i + 1).map(
                    (page) => (
                      <Button
                        key={page}
                        variant={page === currentPage ? "default" : "outline"}
                        size="sm"
                        className="h-8 w-8 p-0"
                        onClick={() => setCurrentPage(page)}
                      >
                        {page}
                      </Button>
                    ),
                  )}
                </div>
              )}
            </div>
          </div>
        </main>
      )}

      <Footer />
    </div>
  );
}
